import { Fragment, type ReactNode } from "react";
import type { Dataset, DatasetLabel } from "@/api";
import { DatasetPreview } from "@/components/dataset";
import { labelValueText } from "@/components/label";
import { formatBytes, formatNumber } from "@/utils";
import { formatDate, parseUtcMs } from "@/utils/datetime";
import { totalDurationText } from "./dataDashboardModel";
import styles from "./DatasetListRow.module.css";

// 개요 페이지 목록 카드(최근 등록, 분량 상위)의 행 하나. 두 카드는 같은 골격(섬네일 · 이름 +
// task · 메타 한 줄 · 기준 값)을 쓰고 강조 값만 다르다. 데이터셋을 구분하는 값은 메타 줄에 두고,
// 그 카드의 순위·최신 기준 값 하나만 행 끝에 둔다. 이름 줄의 남는 폭은 task 라벨이 쓴다.
//
// c6 `ListView` 의 renderItem 은 상호작용이 없는 영역이다(aria-hidden · pointer-events: none 이
// 적용되고, 선택 버튼이 행 전체를 덮는다). 그래서 여기에는 버튼·링크를 두지 않는다. 상세 창
// 열기는 행 클릭과 행 끝 액션이 담당하고, 이 파일은 그리기만 한다.

/** 카드가 강조하는 기준. `recent` 는 등록일, `duration` 은 총 길이를 행 끝에 둔다. */
export type DatasetListRowMode = "recent" | "duration";

export function DatasetListRow({
  dataset,
  mode,
  onThumbnailError,
}: {
  dataset: Dataset;
  mode: DatasetListRowMode;
  /** 대표 섬네일 presigned URL 이 만료되면(onError) 카드가 디바운스해 다시 조회한다. */
  onThumbnailError?: () => void;
}): ReactNode {
  // 메타 줄은 "누구의 어떤 로봇이 몇 편" 까지만 표시한다. 길이·용량 같은 눈금 값은 행 끝 블록이
  // 표시한다. 한 줄에 다섯 조각을 이으면 폭이 모자라 embodiment 가 `sigma_a…` 로 잘린다.
  // 줄이 넘칠 때 줄어드는 조각도 embodiment 하나다. 가장 길고, 앞부분만 봐도 로봇을 구분할 수
  // 있다. 한 문자열로 이어 붙이면 말줄임이 끝부분을 잘라 수치가 잘린 채 남는다.
  const meta = [
    { key: "operator", text: dataset.operator_member?.display_name ?? "담당자 미지정" },
    { key: "embodiment", text: dataset.embodiment.name, shrink: true },
    { key: "episodes", text: `${formatNumber(dataset.total_episodes)} episodes` },
  ];
  // 행 끝 블록. 위 줄이 그 카드의 기준 값, 아래 줄이 나머지 눈금 값이다. 용량은 KPI 행이 세는 네
  // 값(개수·에피소드·길이·용량) 중 행에만 없는 값이라 여기에 표시한다. 미측정은 0 이 아니라 아직
  // 측정하지 않았다는 뜻이다. 그래서 길이와 같은 대시로 적는다.
  const duration = totalDurationText(dataset.total_duration_sec);
  const size = formatBytes(dataset.total_size_bytes, "—");
  const axis =
    mode === "duration"
      ? { main: duration, sub: size, strong: true }
      : { main: formatDate(parseUtcMs(dataset.created_at)), sub: `${duration} · ${size}` };
  const task = taskText(dataset.labels);
  return (
    <div className={styles.row}>
      <DatasetPreview
        label={dataset.display_name}
        imageUrl={dataset.thumbnail_url}
        mode="table"
        onImageError={onThumbnailError}
      />
      <div className={styles.body}>
        <span className={styles.title}>
          <span className={styles.name}>{dataset.display_name}</span>
          {task === null ? null : <span className={styles.task}>{task}</span>}
        </span>
        <span className={styles.meta}>
          {meta.map((part, index) => (
            <Fragment key={part.key}>
              {index === 0 ? null : <span className={styles.metaSep}>·</span>}
              <span className={part.shrink ? styles.metaShrink : undefined}>{part.text}</span>
            </Fragment>
          ))}
        </span>
      </div>
      <span className={styles.axisBlock}>
        <span className={axis.strong === true ? styles.axisStrong : styles.axis}>{axis.main}</span>
        <span className={styles.axisSub}>{axis.sub}</span>
      </span>
    </div>
  );
}

// 어떤 작업의 데이터인지 표시한다. 개요 히트맵(작업 × Embodiment)의 행 축과 같은 값이다. 한
// 데이터셋이 여러 task 를 가질 수 있어서 첫 값만 적고 나머지는 개수로 줄인다. 라벨이 없으면 자리를
// 비운다. 이름 오른쪽에 대시가 있으면 이름의 일부처럼 읽힌다.
function taskText(labels: readonly DatasetLabel[]): string | null {
  const tasks = labels.filter((label) => label.key === "task");
  const [first] = tasks;
  if (first === undefined) {
    return null;
  }
  const head = labelValueText("task", first.value);
  return tasks.length === 1 ? head : `${head} +${tasks.length - 1}`;
}
